import React, { useEffect, useState, useCallback } from "react";
import "./ProfilePage.css";
import PatientForm from "./PatientForm";
import AppointmentSelectSlotPage from './AppointmentSelectSlotPage';
import { useAuth } from './../../AuthProvider';
import { AiOutlineClose } from 'react-icons/ai';
import { AiOutlinePlus } from 'react-icons/ai';
import Avatar from '@mui/material/Avatar';
import { useApp } from "../../AppProvider";
import api from "./../../axiosInstance"
import logger from "../../utils/logger";

function ProfilePage({ onClose, countryCode, mobileNumber }) {
    const { user } = useAuth();
    const { stringAvatar, stringToColor } = useApp();
    const [isPatientFormOpen, setIsPatientFormOpen] = useState(false);
    const [isAppointmentSelectDatePopupOpen, setIsAppointmentSelectDatePopupOpen] = useState(false);
    const [availableProfile, setAvailableProfile] = useState([]);
    const [selectedProfile, setSelectedProfile] = useState();
    const [hoveredCard, setHoveredCard] = useState(null);
    const [hoveredAdd, setHoveredAdd] = useState(false);

    useEffect(() => {
        logger.debug("User's Phone Number:", `${countryCode}${mobileNumber}`);
    }, [`${countryCode}${mobileNumber}`]);

    const fetchProfileData = useCallback(async () => {
        try {
            const phoneNumber = `${countryCode}${mobileNumber}`
            const response = await api.get(`/patientUser/profilesdatabyphoneNumber/${phoneNumber}`)
            const data = response.data
            logger.debug("Profile Data:", data);
            setAvailableProfile(data);
        } catch (error) {
            logger.error("Error fetching profile data:", error);
        }
    }, [user?._id]);

    useEffect(() => {
        fetchProfileData();
    }, [fetchProfileData]);

    return (
        <div style={S.backdrop}>
            <div style={S.modal}>

                {/* ── Header ── */}
                <div style={S.mhead}>
                    <div>
                        <div style={S.mheadTitle}>Choose Profile</div>
                        <div style={S.mheadSub}>{countryCode} {mobileNumber} · Select the patient's profile to continue</div>
                    </div>
                    <AiOutlineClose
                        size={18}
                        style={{ color: "#8899aa", cursor: "pointer", flexShrink: 0 }}
                        onClick={onClose}
                    />
                </div>

                {/* ── Body ── */}
                <div style={S.mbody}>
                    {availableProfile.length > 0 ? (
                        <>
                            <div style={S.sectionTitle}>Available profiles</div>
                            <div style={S.profileGrid}>
                                {availableProfile.map((profile, index) => (
                                    <div
                                        key={index}
                                        style={{
                                            ...S.profileCard,
                                            ...(hoveredCard === index ? S.profileCardHover : {}),
                                        }}
                                        onMouseEnter={() => setHoveredCard(index)}
                                        onMouseLeave={() => setHoveredCard(null)}
                                        onClick={() => { setIsAppointmentSelectDatePopupOpen(true); setSelectedProfile(profile); }}
                                    >
                                        <Avatar
                                            sx={{
                                                width: { md: 56, lg: 64, xl: 64 },
                                                height: { md: 56, lg: 64, xl: 64 },
                                                bgcolor: profile.img ? 'transparent' : stringToColor(profile.name.firstName),
                                            }}
                                            alt={profile.name.firstName}
                                            src={profile.img}
                                        >
                                            {!profile.img && stringAvatar(`${profile.name.firstName}`).children}
                                        </Avatar>
                                        <div style={S.profileDetails}>
                                            <p style={S.profileName}>{`${profile.name.firstName} ${profile.name.lastName}`}</p>
                                            <p style={S.profileAge}>{profile.age} years</p>
                                        </div>
                                    </div>
                                ))}

                                {/* Add Profile card */}
                                <div
                                    style={{
                                        ...S.addCard,
                                        ...(hoveredAdd ? S.addCardHover : {}),
                                    }}
                                    onMouseEnter={() => setHoveredAdd(true)}
                                    onMouseLeave={() => setHoveredAdd(false)}
                                    onClick={() => setIsPatientFormOpen(true)}
                                >
                                    <div style={{ ...S.addCircle, ...(hoveredAdd ? S.addCircleHover : {}) }}>
                                        <AiOutlinePlus size={22} />
                                    </div>
                                    <p style={S.addLabel}>Add Profile</p>
                                </div>
                            </div>
                        </>
                    ) : (
                        <>
                            <div style={S.errorBox}>
                                The phone number <strong>{countryCode} {mobileNumber}</strong> is not registered with any profile. Add a patient profile to continue booking an appointment.
                            </div>
                            <div style={S.profileGrid}>
                                <div
                                    style={{
                                        ...S.addCard,
                                        ...(hoveredAdd ? S.addCardHover : {}),
                                    }}
                                    onMouseEnter={() => setHoveredAdd(true)}
                                    onMouseLeave={() => setHoveredAdd(false)}
                                    onClick={() => setIsPatientFormOpen(true)}
                                >
                                    <div style={{ ...S.addCircle, ...(hoveredAdd ? S.addCircleHover : {}) }}>
                                        <AiOutlinePlus size={22} />
                                    </div>
                                    <p style={S.addLabel}>Add Profile</p>
                                </div>
                            </div>
                        </>
                    )}
                </div>

                {/* ── Footer ── */}
                <div style={S.mfoot}>
                    <button style={S.btnCancelRed} onClick={onClose}>Cancel</button>
                </div>

            </div>

            {isPatientFormOpen && (
                <div className="main-appointment_container-pairent_class">
                    <PatientForm
                        countryCode={countryCode}
                        mobileNumber={mobileNumber}
                        setIsPatientFormOpen={setIsPatientFormOpen}
                        fetchProfiles={fetchProfileData}
                    />
                </div>
            )}

            {isAppointmentSelectDatePopupOpen && (
                <AppointmentSelectSlotPage
                    selectedProfile={selectedProfile}
                    setIsAppointmentSelectDatePopupOpen={setIsAppointmentSelectDatePopupOpen}
                    onClose={onClose}
                />
            )}
        </div>
    );
}

export default ProfilePage;

// ─── Styles (matches AppointmentSelectSlotPage) ───────────────────────────────
const S = {
    backdrop: { position: "fixed", inset: 0, background: "rgba(26,42,58,0.5)", zIndex: 999, display: "flex", alignItems: "center", justifyContent: "center", padding: 20, fontFamily: "'DM Sans',system-ui,sans-serif" },
    modal: { background: "#fff", borderRadius: 16, width: "100%", maxWidth: 680, overflow: "hidden", maxHeight: "92vh", overflowY: "auto" },
    mhead: { background: "#023047", padding: "16px 22px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 },
    mheadTitle: { color: "#fff", fontSize: 15, fontWeight: 600 },
    mheadSub: { color: "#8899aa", fontSize: 11, marginTop: 3 },
    mbody: { padding: "22px 24px" },
    mfoot: { padding: "14px 24px", borderTop: "0.5px solid #e8eaed", display: "flex", justifyContent: "flex-start", alignItems: "center" },
    sectionTitle: { fontSize: 11, fontWeight: 500, color: "#6b7280", textTransform: "uppercase", letterSpacing: ".05em", marginBottom: 16 },

    profileGrid: { display: "flex", flexWrap: "wrap", gap: 16 },

    profileCard: { width: 148, height: 178, border: "0.5px solid #e8eaed", borderRadius: 12, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 12, cursor: "pointer", transition: "border-color 0.2s, box-shadow 0.2s, transform 0.15s", background: "#fff", padding: "16px 10px", boxSizing: "border-box" },
    profileCardHover: { borderColor: "#219EBC", boxShadow: "0 6px 20px rgba(33,158,188,0.15)", transform: "translateY(-2px)" },

    profileDetails: { textAlign: "center" },
    profileName: { fontSize: 13, fontWeight: 600, color: "#1a2a3a", margin: 0 },
    profileAge: { fontSize: 11, color: "#9ca3af", margin: "4px 0 0 0" },

    addCard: { width: 148, height: 178, border: "1.5px dashed #b5d9e4", borderRadius: 12, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 10, cursor: "pointer", transition: "border-color 0.2s, background 0.2s", boxSizing: "border-box" },
    addCardHover: { borderColor: "#219EBC", background: "#f0fafd" },

    addCircle: { width: 48, height: 48, borderRadius: "50%", background: "#e8f6f9", display: "flex", alignItems: "center", justifyContent: "center", color: "#219EBC", transition: "background 0.2s, color 0.2s" },
    addCircleHover: { background: "#219EBC", color: "#fff" },
    addLabel: { fontSize: 12, fontWeight: 600, color: "#219EBC", margin: 0 },

    errorBox: { background: "#fff5f5", border: "0.5px solid #fecaca", borderRadius: 10, padding: "12px 16px", color: "#b91c1c", fontSize: 13, lineHeight: 1.6, marginBottom: 20 },

    btnCancelRed: { height: 36, padding: "0 18px", border: "none", borderRadius: 8, background: "#fee2e2", color: "#b91c1c", fontSize: 13, fontWeight: 500, cursor: "pointer", fontFamily: "inherit" },
};
